import { z } from 'zod';
import { eventTimestampSchema, identitySchema, visitorIdSchema } from './common.js';
// --- Consent (Google Consent Mode v2) ---
export const consentStateSchema = z.enum(['granted', 'denied', 'unspecified']);
export const consentSchema = z
    .object({
    ad_user_data: consentStateSchema,
    ad_personalization: consentStateSchema,
    ad_storage: consentStateSchema.optional(),
    analytics_storage: consentStateSchema.optional(),
})
    .meta({
    id: 'Consent',
    description: 'Google Consent Mode v2 signals. Required for EEA/UK traffic.',
});
// --- Click IDs, marketing cookies, affiliate params ---
/** All known click IDs captured from URLs. Record shape avoids schema churn as platforms change. */
export const clickIdsSchema = z
    .record(z.string().min(1).max(64), z.string().min(1).max(500))
    .refine((obj) => Object.keys(obj).length <= 100, {
    error: 'click_ids must have at most 100 keys',
})
    .meta({ id: 'ClickIds', description: 'Ad platform click IDs captured from landing page URL' });
/** Marketing, CRM, and analytics cookie values read server-side from Cookie: header. */
export const marketingCookiesSchema = z
    .record(z.string().min(1).max(64), z.string().min(1).max(500))
    .refine((obj) => Object.keys(obj).length <= 50, {
    error: 'marketing_cookies must have at most 50 keys',
})
    .meta({
    id: 'MarketingCookies',
    description: 'First-party marketing/CRM cookie values (server-read)',
});
/** Affiliate network params — prefixed by network (e.g. aff_impact_irclickid). */
export const affiliateParamsSchema = z
    .record(z.string().min(1).max(64), z.string().min(1).max(500))
    .refine((obj) => Object.keys(obj).length <= 80, {
    error: 'affiliate_params must have at most 80 keys',
})
    .meta({
    id: 'AffiliateParams',
    description: 'Affiliate network params keyed aff_<network>_<param>',
});
// --- Browser-side context ---
export const browserContextSchema = z
    .object({
    timezone: z.string().max(100).optional(),
    language: z.string().max(35).optional(),
    languages: z.array(z.string().max(35)).max(10).optional(),
    screen_width: z.number().int().min(0).max(32768).optional(),
    screen_height: z.number().int().min(0).max(32768).optional(),
    viewport_width: z.number().int().min(0).max(32768).optional(),
    viewport_height: z.number().int().min(0).max(32768).optional(),
    device_pixel_ratio: z.number().min(0).max(10).optional(),
    color_depth: z.number().int().min(0).max(48).optional(),
    hardware_concurrency: z.number().int().min(0).max(256).optional(),
    device_memory: z.number().min(0).max(64).optional(),
    connection_type: z.enum(['slow-2g', '2g', '3g', '4g', 'unknown']).optional(),
    prefers_color_scheme: z.enum(['light', 'dark', 'no-preference']).optional(),
    prefers_reduced_motion: z.enum(['reduce', 'no-preference']).optional(),
})
    .meta({ id: 'BrowserContext', description: 'Browser-reported environment fields' });
// --- Event type → BigQuery table mapping (Single Source of Truth) ---
export const EVENT_TABLES = {
    pageview: 'pageviews',
    interaction: 'interactions',
    custom: 'custom_events',
    experiment_assignment: 'experiment_assignments',
    flag_evaluation: 'flag_evaluations',
    sdk_error: 'sdk_errors',
};
// --- Shared base fields ---
const baseEventFields = {
    // A1: Client-minted UUIDv4 for CAPI dedup. If omitted, server mints UUIDv7.
    event_id: z.string().uuid().optional().meta({
        description: 'Client-minted UUID for CAPI dedup. If omitted, server mints UUIDv7.',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    visitor_id: visitorIdSchema,
    timestamp: eventTimestampSchema,
    session_id: z
        .string()
        .max(255)
        .optional()
        .meta({ description: 'Session identifier for grouping events' }),
    url: z.string().url().max(2048).optional(),
    identity: identitySchema.optional(),
    // A2: Google Consent Mode v2 signals
    consent: consentSchema.optional().meta({
        description: 'Google Consent Mode v2 signals',
    }),
    // A4: Ad platform click IDs, marketing cookies, affiliate params, extended UTMs
    click_ids: clickIdsSchema.optional(),
    marketing_cookies: marketingCookiesSchema.optional(),
    affiliate_params: affiliateParamsSchema.optional(),
    affiliate_network: z.string().min(1).max(50).optional().meta({
        description: "Affiliate network classifier output (e.g. 'impact', 'shareasale'). Server-set; SDK should not set this.",
    }),
    // Extended UTMs (GA4-aligned)
    utm_id: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM campaign ID — required for GA4 data-import joins' }),
    utm_source_platform: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM buying platform (e.g. DV360, SA360)' }),
    utm_creative_format: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM creative type (display/native/video/search)' }),
    utm_marketing_tactic: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM marketing tactic (remarketing/prospecting)' }),
    // A5: Browser-side context
    browser: browserContextSchema.optional(),
};
// --- Pageview ---
export const pageviewEventSchema = z
    .object({
    type: z.literal('pageview'),
    ...baseEventFields,
    referrer: z.string().max(2048).optional().meta({ description: 'HTTP referrer URL' }),
    utm_source: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM source attribution parameter' }),
    utm_medium: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM medium attribution parameter' }),
    utm_campaign: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM campaign attribution parameter' }),
    utm_content: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM content attribution parameter' }),
    utm_term: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM term attribution parameter' }),
})
    .meta({ id: 'PageviewEvent', description: 'Page view event with UTM attribution' });
// --- Interaction ---
export const elementSchema = z
    .object({
    tag: z.string().max(50).meta({ description: 'HTML tag name', example: 'button' }),
    text: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'Visible text content (truncated to 500 chars)' }),
    id: z.string().max(500).optional(),
    classes: z.string().max(1000).optional().meta({ description: 'CSS classes (space-separated)' }),
    href: z.string().max(2048).optional().meta({ description: 'href attribute for links' }),
    name: z.string().max(500).optional().meta({ description: 'name attribute for form elements' }),
})
    .meta({ id: 'InteractionElement', description: 'DOM element that was interacted with' });
export const interactionTypeSchema = z.enum([
    'click',
    'form_submit',
    'form_start',
    'scroll',
    'outbound_click',
    'file_download',
]);
export const interactionEventSchema = z
    .object({
    type: z.literal('interaction'),
    ...baseEventFields,
    interaction_type: interactionTypeSchema,
    element: elementSchema.optional(),
    scroll_depth: z
        .number()
        .int()
        .refine((n) => [25, 50, 75, 90].includes(n), {
        error: 'Scroll depth must be 25, 50, 75, or 90',
    })
        .optional()
        .meta({ description: 'Scroll percentage threshold (25, 50, 75, or 90)' }),
    file_extension: z
        .string()
        .max(20)
        .optional()
        .meta({ description: 'File extension for download events', example: 'pdf' }),
    is_outbound: z.boolean().optional().meta({ description: 'True for clicks on external links' }),
})
    .meta({
    id: 'InteractionEvent',
    description: 'User interaction event (click, scroll, form, etc.)',
});
// --- Custom event ---
export const eventPropertiesSchema = z
    .record(z.string().max(100), z.union([z.string().max(1000), z.number(), z.boolean()]))
    .refine((obj) => Object.keys(obj).length <= 50, {
    error: 'Properties must have at most 50 keys',
})
    .meta({ description: 'Arbitrary key-value properties (max 50 keys)' });
export const customEventSchema = z
    .object({
    type: z.literal('custom'),
    ...baseEventFields,
    event_name: z
        .string()
        .min(1)
        .max(100)
        .regex(/^\S+$/, 'No whitespace allowed')
        .meta({ description: 'Custom event name', example: 'add_to_cart' }),
    properties: eventPropertiesSchema.optional(),
    // A6: Conversion fields — typed columns for BQ joinability
    order_id: z
        .string()
        .min(1)
        .max(100)
        .optional()
        .meta({ description: 'Order or transaction ID for conversion events' }),
    value: z
        .number()
        .min(0)
        .max(1_000_000)
        .optional()
        .meta({ description: 'Monetary value of the conversion event' }),
    currency: z
        .string()
        .length(3)
        .regex(/^[A-Z]{3}$/, 'Must be ISO 4217 uppercase currency code (e.g. USD)')
        .optional()
        .meta({ description: 'ISO 4217 currency code (uppercase, e.g. USD)', example: 'USD' }),
    quantity: z
        .number()
        .int()
        .min(1)
        .max(10_000)
        .optional()
        .meta({ description: 'Quantity for purchase/cart events' }),
})
    .meta({ id: 'CustomEvent', description: 'Developer-defined custom event' });
// --- Discriminated union ---
export const eventSchema = z.discriminatedUnion('type', [
    pageviewEventSchema,
    interactionEventSchema,
    customEventSchema,
]);
// --- Batch request (outer envelope) ---
export const eventBatchRequestSchema = z
    .object({
    events: z
        .array(eventSchema)
        .min(1)
        .max(100)
        .meta({ description: 'Array of tracking events (pageview, interaction, or custom)' }),
})
    .meta({ id: 'EventBatchRequest', description: 'Batch of 1-100 tracking events' });
//# sourceMappingURL=events.js.map